import { useParams, Link } from 'react-router-dom';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import HeartDivider from '../components/HeartDivider';
import { PageLoader, EmptyState } from '../components/ui';
import { useAsync } from '../lib/useAsync';
import { sanitize } from '../lib/sanitize';
import { pagesApi } from '../api/resources';
import { assetUrl } from '../api/client';

/**
 * Public renderer for CMS / Page-Maker pages.
 *
 * Route: /pages/:slug (matches Menu Manager's resolved_url).
 * Fetches via pagesApi.get(slug). The public-read route has optional auth, so an
 * authenticated admin/owner can load a NON-published (draft) page for preview;
 * guests only ever receive published pages (otherwise 403/404).
 *
 * Each saved component is `{ id, type, position, content }` (see
 * DynamicPageResource). We map content defensively (`content ?? data ?? props`)
 * and render blocks sorted by `position`.
 */

const enc = encodeURIComponent;

/** Convert a YouTube/Vimeo watch URL to an embeddable URL, else return null. */
function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
      if (u.pathname.startsWith('/embed/')) return url;
    }
    if (host === 'vimeo.com') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
    if (host === 'player.vimeo.com') return url;
  } catch {
    return null;
  }
  return null;
}

function BlockHero({ c }) {
  const heading = c.heading || c.title;
  const sub = c.subheading || c.subtitle || c.text;
  const ctaLabel = c.cta || c.button || c.cta_label;
  const ctaUrl = c.cta_url || c.url || c.link;
  if (!heading && !sub) return null;
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-maroon-800 to-maroon-950">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gold-400/10 blur-2xl"
      />
      <Reveal className="container-page relative py-16 text-center sm:py-20">
        {heading && (
          <>
            <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl">
              {heading}
            </h2>
            <HeartDivider className="mt-5 justify-center text-gold-400" />
          </>
        )}
        {sub && <p className="mx-auto mt-4 max-w-2xl text-lg text-cream-200">{sub}</p>}
        {ctaLabel && (
          ctaUrl ? (
            <a href={ctaUrl} className="btn-gold mt-6 inline-flex" target="_blank" rel="noopener noreferrer">
              {ctaLabel}
            </a>
          ) : (
            <span className="btn-gold mt-6 inline-flex">{ctaLabel}</span>
          )
        )}
      </Reveal>
    </section>
  );
}

function BlockText({ c }) {
  const text = c.content || c.text || c.body;
  if (!text) return null;
  return (
    <section className="container-page py-10">
      <p className="mx-auto max-w-3xl whitespace-pre-line text-lg leading-relaxed text-ink-soft">{text}</p>
    </section>
  );
}

function BlockRichText({ c }) {
  const html = c.html || c.richtext || c.rich_text || c.content || c.body;
  if (!html) return null;
  return (
    <section className="container-page py-10">
      <div
        className="prose-zonta mx-auto max-w-3xl leading-relaxed"
        dangerouslySetInnerHTML={{ __html: sanitize(html) }}
      />
    </section>
  );
}

function BlockGallery({ c }) {
  const raw = c.images || c.items || [];
  const images = raw
    .map((m) => (typeof m === 'string' ? { url: m } : m))
    .map((m) => ({ url: assetUrl(m.url ?? m.path ?? m.src), alt: m.alt || m.caption || '' }))
    .filter((m) => m.url);
  if (!images.length) return null;
  return (
    <section className="container-page py-10">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {images.map((m, i) => (
          <Reveal key={i} delay={i * 60} className="arch overflow-hidden shadow-card">
            <img src={m.url} alt={m.alt} loading="lazy" className="aspect-square w-full object-cover" />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function BlockVideo({ c }) {
  const url = c.url || c.src || c.embed;
  if (!url) return null;
  const embed = toEmbedUrl(url);
  return (
    <section className="container-page py-10">
      <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl bg-ink shadow-lift">
        <div className="relative aspect-video w-full">
          {embed ? (
            <iframe
              src={embed}
              title={c.title || 'Embedded video'}
              className="absolute inset-0 h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          ) : (
            <video controls src={url} className="absolute inset-0 h-full w-full">
              <track kind="captions" />
            </video>
          )}
        </div>
      </div>
    </section>
  );
}

function BlockFile({ c }) {
  const url = c.url || c.href || c.file;
  const label = c.label || c.title || 'Download file';
  if (!url) return null;
  return (
    <section className="container-page py-8">
      <div className="mx-auto max-w-3xl">
        <a href={url} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2">
          <span aria-hidden="true">⬇</span> {label}
        </a>
        {c.description && <p className="mt-2 text-sm text-ink-muted">{c.description}</p>}
      </div>
    </section>
  );
}

function BlockCards({ c }) {
  const cards = c.cards || c.items || [];
  if (!cards.length) return null;
  return (
    <section className="container-page py-10">
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card, i) => {
          const link = card.link || card.url;
          const inner = (
            <>
              {card.title && <h3 className="text-lg font-semibold text-ink">{card.title}</h3>}
              {(card.text || card.description) && (
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">{card.text || card.description}</p>
              )}
              {link && <span className="link-caps mt-3">Learn more <span aria-hidden="true">&rsaquo;</span></span>}
            </>
          );
          return (
            <Reveal key={i} delay={i * 80} className="card card-hover p-6">
              {link ? (
                <a href={link} target="_blank" rel="noopener noreferrer" className="block">
                  {inner}
                </a>
              ) : (
                inner
              )}
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function BlockCta({ c }) {
  const text = c.text || c.heading || c.title;
  const label = c.button || c.cta || c.cta_label;
  const url = c.url || c.cta_url || c.link;
  if (!text && !label) return null;
  return (
    <section className="container-page py-10">
      <div className="flex flex-col items-center justify-between gap-5 rounded-2xl bg-maroon-700 px-8 py-10 text-center shadow-lift sm:flex-row sm:text-left">
        {text && <p className="font-display text-xl font-bold text-white sm:text-2xl">{text}</p>}
        {label &&
          (url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="btn-gold shrink-0">
              {label}
            </a>
          ) : (
            <span className="btn-gold shrink-0">{label}</span>
          ))}
      </div>
    </section>
  );
}

function BlockFaq({ c }) {
  const items = c.items || c.faqs || [];
  if (!items.length) return null;
  return (
    <section className="container-page py-10">
      <div className="mx-auto max-w-3xl space-y-3">
        {items.map((item, i) => {
          const q = item.q || item.question;
          const a = item.a || item.answer;
          if (!q) return null;
          return (
            <details key={i} className="card group p-5 open:ring-1 open:ring-maroon-200">
              <summary className="flex cursor-pointer list-none items-center justify-between font-semibold text-ink">
                {q}
                <span aria-hidden="true" className="ml-4 text-maroon-700 transition-transform group-open:rotate-45">
                  +
                </span>
              </summary>
              {a && <p className="mt-3 text-sm leading-relaxed text-ink-muted">{a}</p>}
            </details>
          );
        })}
      </div>
    </section>
  );
}

const RENDERERS = {
  hero: BlockHero,
  text: BlockText,
  richtext: BlockRichText,
  'rich-text': BlockRichText,
  gallery: BlockGallery,
  video: BlockVideo,
  file: BlockFile,
  'file-download': BlockFile,
  card: BlockCards,
  cards: BlockCards,
  cta: BlockCta,
  faq: BlockFaq,
};

function Block({ type, content }) {
  const Renderer = RENDERERS[type];
  if (!Renderer) return null; // unknown type -> skip gracefully
  return <Renderer c={content || {}} />;
}

export default function PageView() {
  const { slug } = useParams();
  const { data: page, loading, error } = useAsync(() => pagesApi.get(slug), [slug]);

  if (loading) return <PageLoader label="Loading page…" />;

  if (error || !page) {
    const forbidden = error?.status === 403 || error?.status === 404;
    return (
      <div className="container-page py-16">
        <Seo title="Page not found" />
        <EmptyState
          title={forbidden ? 'Page not available' : 'Something went wrong'}
          message={
            forbidden
              ? "This page doesn't exist or hasn't been published yet."
              : 'We could not load this page. Please try again later.'
          }
          action={
            <Link to="/" className="btn-primary">
              Back to home
            </Link>
          }
        />
      </div>
    );
  }

  const components = [...(page.components || [])]
    .map((c) => ({
      id: c.id,
      type: c.type,
      position: c.position ?? 0,
      content: c.content ?? c.data ?? c.props ?? {},
    }))
    .sort((a, b) => a.position - b.position);

  const isDraft = page.status && page.status !== 'published';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <>
      <Seo title={page.title} description={page.meta_description} type="article" />

      {isDraft && (
        <div className="sticky top-0 z-40 bg-gold-400 px-4 py-2 text-center text-sm font-semibold text-maroon-950 shadow-card">
          Draft preview — this page is not yet published ({page.status}).
        </div>
      )}

      <article className="pb-16">
        <PageHero eyebrow="Zonta D17 Area 1" title={page.title} subtitle={page.meta_description} />

        {components.length === 0 ? (
          <div className="container-page py-12">
            <p className="mx-auto max-w-3xl text-center text-ink-muted">This page has no content yet.</p>
          </div>
        ) : (
          components.map((c) => <Block key={c.id} type={c.type} content={c.content} />)
        )}

        <div className="container-page pt-6">
          <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-2 border-t border-cream-300 pt-6">
            <span className="text-sm font-medium text-ink-muted">Share:</span>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="pill-maroon transition-colors hover:bg-maroon-100"
            >
              Facebook
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(page.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="pill-maroon transition-colors hover:bg-maroon-100"
            >
              X
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="pill-maroon transition-colors hover:bg-maroon-100"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </article>
    </>
  );
}
